/**
 * PHYSICAL-RISK-EDIT-MODAL
 * Add or edit a physical risk from a wizard modal.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"
import LoadingModal from "@/modals/loading.modal"
import UploadPhotoModal from "@/components/UploadPhotoModal"
import Modal from "@/components/modal"
import Button from "@/components/button"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import siteStore from "@/entities/site/model/site.store"
import populationStore from "@/entities/population/model/population.store"
import { Site } from "@/entities/site/model/site.types"
import { Population } from "@/entities/population/model/population.types"
import { createEmptyRiskFamily, RISK_FAMILY_UNDEFINED_ID } from "@/entities/risk-family/model/risk-family.types"
import { createEmptyPhysicalRisk, NEWLY_ADDED_RISK_PARAM, PhysicalRisk } from "../../model/physical-risk.types"
import { PhysicalRiskAddContext } from "../../model/physical-risk-add-context.types"
import physicalRiskStore from "../../model/physical-risk.store"
import { getLowestPreventionValue } from "../../model/physical-risk.utils"
import PhysicalRiskPopulationCatalogModal from "./PhysicalRiskPopulationCatalogModal"
import { faPlus, faTrashAlt, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { PHYSICAL_RISK_UPDATED_AT_SORT } from "../../model/physical-risk-sort.constants"
import { STATUS_SAVING } from "@/redux/_status.types"
import { FadeIn } from "@/utils/animations.utils"
import PhysicalRiskDescriptionStep from "../wizard/PhysicalRiskDescriptionStep"
import PhysicalRiskEvaluationStep from "../wizard/PhysicalRiskEvaluationStep"
import PhysicalRiskOverviewStep from "../wizard/PhysicalRiskOverviewStep"
import PhysicalRiskPerimeterStep from "../wizard/PhysicalRiskPerimeterStep"
import PhysicalRiskPreventionStep from "../wizard/PhysicalRiskPreventionStep"
import PhysicalRiskScale from "@/entities/physical-risk/ui/PhysicalRiskScale"
import Space from "@/components/space"
import PhysicalRiskWorkUnitStep from "../wizard/PhysicalRiskWorkUnitStep"
import PhysicalRiskSelectStep from "../wizard/PhysicalRiskSelectStep"
import { usePhysicalRiskAssistant } from "../../hooks/usePhysicalRiskAssistant"
import PhysicalRiskDeleteModal from "./PhysicalRiskDeleteModal"
import PreventionActionAddModal from "@/entities/prevention-action/ui/modal/PreventionActionAddModal"
import RiskFamilyCard from "@/entities/risk-family/ui/RiskFamilyCard"
import RiskFamilyEditModal from "@/entities/risk-family/ui/modal/RiskFamilyEditModal"
import { WIZARD_STEPS, WizardStep } from "../wizard/physical-risk-wizard.constants"
import { getWizardStepLockReason } from "@/components/wizard.utils"
import {
  getInPlacePreventionIds,
  getInPlacePreventionSignature,
  isPreventionOnlyReinforced,
  usePreventionLevelAssistant
} from "../../hooks/usePreventionLevelAssistant"
import PhysicalRiskCalculationsToggle from "../wizard/PhysicalRiskCalculationsToggle"
import PhysicalRiskCalculationDetails from "../wizard/PhysicalRiskCalculationDetails"
import { useLocation, useNavigate } from "react-router-dom"
import { RISK_FAMILIES_PATH } from "@/entities/risk-family/model/risk-family.utils"
import { useRisksSpace } from "@/core/risks-space.context"

interface Props {
  context?: PhysicalRiskAddContext
  initialStep?: WizardStep //Etape ouverte a l'edition d'un risque existant (defaut : la vue d'ensemble)
  onClose: () => void
  risk?: PhysicalRisk
}

type InternalStep = "WORK_UNIT" | "SELECT" | "CATALOG" | "PHOTO" | "OVERVIEW" | WizardStep
type EditOverlay = "ADD_FAMILY" | "DELETE" | "EDIT_FAMILY" | "PREVENTION_ACTION_ADD" | null

const PERIMETER_WIZARD_OWL_TEXT = "Où ce risque est-il présent ou qui concerne-t-il ?"
const DESCRIPTION_WIZARD_OWL_TEXT = "Décrivez la situation à risque"
const PREVENTION_EMPTY_WIZARD_OWL_TEXT = "Quelles mesures de prévention existent pour ce risque ?"
const PREVENTION_DETAILS_WIZARD_OWL_TEXT = "Définissez des mesures de prévention."

export default function PhysicalRiskEditModal(props: Props) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  //Lecture seule (document unique) : la meme vue d'ensemble, sans rien pour la modifier.
  const { getPath, isReadonly } = useRisksSpace()
  const location = useLocation()

  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const activeEstablishment = useAppSelector(establishmentStore.selectors.active)
  const riskFamilies = useAppSelector(riskFamilyStore.selectors.list)
  const draftRisk = useAppSelector(physicalRiskStore.selectors.draft)
  const status = useAppSelector(physicalRiskStore.selectors.status)
  const actionsForRisk = useAppSelector((state) => preventionActionStore.selectors.listForRisk(state, draftRisk.id))
  const activeSite = useAppSelector(siteStore.selectors.active)
  const activePopulation = useAppSelector(populationStore.selectors.active)
  const activeFamily = useAppSelector(riskFamilyStore.selectors.active)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const selectedPopulationIds = useAppSelector(populationStore.selectors.selected)
  const selectedRiskIds = useAppSelector(physicalRiskStore.selectors.selected)

  const context = useMemo<PhysicalRiskAddContext>(() => {
    if (props.context) return props.context
    if (activeSite?.EstablishmentId) return { type: "site", site: activeSite }
    if (activePopulation?.EstablishmentId) return { type: "population", population: activePopulation }
    if (activeFamily?.id) return { type: "family", family: activeFamily }
    return { type: "generic" }
  }, [props.context, activeSite, activePopulation, activeFamily])
  const isNewRisk = !props.risk

  const [step, setStep] = useState<InternalStep>(() =>
    isNewRisk
      ? context.type === "site" || context.type === "population"
        ? "SELECT"
        : "WORK_UNIT"
      : (props.initialStep ?? "OVERVIEW")
  )
  const [childModal, setChildModal] = useState<EditOverlay>(null)
  //Signature des mesures en place a l'entree de l'etape Prevention : inchangee a la sortie,
  //il n'y a rien a reevaluer et la valeur choisie a la main est preservee
  const preventionSignature = useRef<string | null>(null)
  //Mesures en place a l'entree de l'etape : si aucune n'a disparu, le niveau retenu fait plancher
  const preventionInPlaceIds = useRef<string[]>([])
  const { isSuggestingPrevention, suggestPreventionLevel } = usePreventionLevelAssistant()
  const [description, setDescription] = useState("")
  const [linkedSites, setLinkedSites] = useState<Site[]>(() => (context.type === "site" ? [context.site] : []))
  const [linkedPopulations, setLinkedPopulations] = useState<Population[]>(() =>
    context.type === "population" ? [context.population] : []
  )
  const [workUnitType, setWorkUnitType] = useState<"site" | "population" | null>(null)

  const {
    assistantLoadingText,
    descriptionPlaceholder,
    descriptionStartMode,
    handleGenerateFromDescription,
    handleUploadImage,
    isAssistantLoading,
    openDescriptionMode,
    setDescriptionStartMode
  } = usePhysicalRiskAssistant({
    applyContextToDraft,
    context,
    createDraftWithContext,
    description,
    dispatch,
    getContextFamilyId,
    linkedPopulations,
    linkedSites,
    openEditStep,
    accountRisksSettings,
    riskFamilies,
    setStep
  })

  useEffect(() => {
    if (step === "PREVENTION") {
      preventionSignature.current = getInPlacePreventionSignature(actionsForRisk)
      preventionInPlaceIds.current = getInPlacePreventionIds(actionsForRisk)
    }
    //A l'entree de l'etape seulement : la signature doit rester celle d'avant les modifications
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])

  useEffect(() => {
    if (!isNewRisk && props.risk) {
      dispatch(physicalRiskStore.actions.setActive(props.risk))
    } else {
      dispatch(physicalRiskStore.actions.setActive(null))
      applyContextToDraft()
    }
  }, [isNewRisk, props.risk?.id])

  function getContextFamilyId() {
    return context.type === "family" && context.family.id !== RISK_FAMILY_UNDEFINED_ID ? context.family.id : null
  }

  function createDraftWithContext(overrides?: Partial<PhysicalRisk>) {
    return createEmptyPhysicalRisk({
      prevention: getLowestPreventionValue(accountRisksSettings),
      siteIds: linkedSites.map((site) => site.id),
      populationIds: linkedPopulations.map((population) => population.id),
      RiskFamilyId: getContextFamilyId(),
      ...overrides
    })
  }

  function openEditStep(risk: PhysicalRisk) {
    dispatch(physicalRiskStore.actions.setActive(risk))
    setStep("DESCRIPTION")
  }

  function applyContextToDraft() {
    dispatch(physicalRiskStore.actions.edit({ key: "prevention", value: getLowestPreventionValue(accountRisksSettings) }))
    dispatch(physicalRiskStore.actions.edit({ key: "siteIds", value: linkedSites.map((site) => site.id) }))
    dispatch(
      physicalRiskStore.actions.edit({
        key: "populationIds",
        value: linkedPopulations.map((population) => population.id)
      })
    )

    if (context.type === "family") {
      dispatch(physicalRiskStore.actions.edit({ key: "RiskFamilyId", value: getContextFamilyId() }))
    }
  }

  function openBlankModal() {
    openEditStep(createDraftWithContext())
  }

  function handleWorkUnitsChange(sites: Site[], populations: Population[]) {
    setDescriptionStartMode("CHOICE")
    setLinkedSites(sites)
    setLinkedPopulations(populations)
    dispatch(physicalRiskStore.actions.edit({ key: "siteIds", value: sites.map((site) => site.id) }))
    dispatch(
      physicalRiskStore.actions.edit({
        key: "populationIds",
        value: populations.map((population) => population.id)
      })
    )
  }

  function keepCreatedPhotoInStore(response) {
    if (!response?.id || !draftRisk.photoId || response.photoId) return

    dispatch(
      physicalRiskStore.actions.edit({
        key: "photoId",
        value: draftRisk.photoId,
        idInList: response.id
      })
    )
  }

  async function saveRisk() {
    if (draftRisk.EstablishmentId === null) {
      const response = await dispatch(physicalRiskStore.thunks.createEntity(draftRisk)).unwrap()
      if (response) await saveDraftPreventionActions()
      if (response) keepCreatedPhotoInStore(response)
      if (response) dispatch(physicalRiskStore.actions.sort(PHYSICAL_RISK_UPDATED_AT_SORT))
      return response
    }

    dispatch(physicalRiskStore.actions.edit({ key: "updatedAt", value: new Date() }))
    const response = await dispatch(physicalRiskStore.thunks.updateEntity()).unwrap()
    if (response) dispatch(physicalRiskStore.actions.sort(PHYSICAL_RISK_UPDATED_AT_SORT))
    return response
  }

  async function saveDraftPreventionActions() {
    const draftActions = actionsForRisk.filter((action) => action.EstablishmentId === null)

    for (const action of draftActions) {
      const response = await dispatch(preventionActionStore.thunks.createEntity(action)).unwrap()
      if (response) {
        dispatch(preventionActionStore.actions.remove(action.id))
        dispatch(preventionActionStore.actions.addMany([response]))
      }
    }
  }

  async function handleSaveAndClose() {
    const response = await saveRisk()
    if (!response) return

    if (isNewRisk) {
      handleStandaloneRiskCreated(response)
      return
    }

    props.onClose()
  }

  //Le risque cree est signale par ?added= : PhysicalRiskCard s'anime partout ou la carte est rendue.
  function handleStandaloneRiskCreated(risk: PhysicalRisk) {
    props.onClose()
    clearFiltersHidingRisk(risk)

    //Hors contexte (accueil, catalogue par famille), aucune page ne garantit d'afficher la carte :
    //la famille du risque est le point de repli ou il est toujours visible.
    const pathname = context.type === "generic"
      ? getPath(`${RISK_FAMILIES_PATH}/${risk.RiskFamilyId ?? RISK_FAMILY_UNDEFINED_ID}`)
      : location.pathname
    const isSamePage = pathname === location.pathname
    const search = new URLSearchParams(isSamePage ? location.search : "")
    search.set(NEWLY_ADDED_RISK_PARAM, risk.id)

    //Sur place le param n'est qu'une annotation de la vue courante : inutile d'empiler un cran d'historique.
    navigate({ pathname, search: search.toString() }, { replace: isSamePage })
  }

  //Un filtre actif peut masquer le risque a peine cree : on l'efface plutot que de ne rien montrer.
  function clearFiltersHidingRisk(risk: PhysicalRisk) {
    const isHidden =
      (selectedSiteIds.length > 0 && !risk.siteIds.some((id) => selectedSiteIds.includes(id))) ||
      (selectedPopulationIds.length > 0 && !risk.populationIds.some((id) => selectedPopulationIds.includes(id))) ||
      (selectedRiskIds.length > 0 && !selectedRiskIds.includes(risk.id))
    if (!isHidden) return

    dispatch(siteStore.actions.select([]))
    dispatch(populationStore.actions.select([]))
    dispatch(physicalRiskStore.actions.select([]))
  }

  function handleSelectFamily(value: null | string) {
    dispatch(physicalRiskStore.actions.edit({ key: "RiskFamilyId", value }))
    setChildModal(null)
  }

  function handleSelectDescriptionStartMode(mode: "catalog" | "describe" | "photo") {
    if (mode === "catalog") setStep("CATALOG")
    else if (mode === "photo") setStep("PHOTO")
    else openDescriptionMode()
  }

  //Pas de titre : l'etape est deja nommee par la sidebar du wizard et par la question de la chouette.
  //Largeur de l'echelle bornee, sinon ses libelles s'etirent sur toute la colonne. L'echelle se
  //serre jusqu'a 280px pour rester a cote du detail : ses libelles passent alors sur deux lignes.
  //La prevention ne passe pas par ici : elle se regle dans l'entonnoir de l'etape Evaluation.
  function renderEvaluationStep(id: "severity" | "frequency") {
    return (
      <div className="mt-20 flex w-full flex-wrap items-start gap-20">
        <div className="min-w-[280px] max-w-[700px] flex-1">
          <PhysicalRiskScale id={id} />
        </div>
        {/* Le detail explique l'echelle affichee : il ne depend pas du lien du pied de modale */}
        <PhysicalRiskCalculationDetails step={id} onEditSeverity={() => setStep("SEVERITY")} />
      </div>
    )
  }

  function renderWizardStep() {
    switch (step) {
      case "PERIMETER":
        return (
          <PhysicalRiskPerimeterStep
            linkedPopulations={linkedPopulations}
            linkedSites={linkedSites}
            onChange={handleWorkUnitsChange}
          />
        )
      case "DESCRIPTION":
        return <PhysicalRiskDescriptionStep onEditFamily={() => setChildModal("EDIT_FAMILY")} />
      case "SEVERITY":
        return renderEvaluationStep("severity")
      case "FREQUENCY":
        return renderEvaluationStep("frequency")
      case "PREVENTION":
        return <PhysicalRiskPreventionStep onAdd={() => setChildModal("PREVENTION_ACTION_ADD")} />
      case "EVALUATION":
        //Le niveau de prevention se choisit dans un entonnoir, pas dans l'echelle en liste
        return (
          <PhysicalRiskEvaluationStep
            onEditFrequency={() => setStep("FREQUENCY")}
            onEditSeverity={() => setStep("SEVERITY")}
          />
        )
      case "OVERVIEW":
      default:
        return (
          <PhysicalRiskOverviewStep
            isReadonly={isReadonly}
            getStepLockReason={getStepLockReason}
            onReviewPrevention={handleReviewPrevention}
            onStepClick={handleWizardStepClick}
          />
        )
    }
  }

  function getActiveWizardStepId(): WizardStep | undefined {
    if (step === "WORK_UNIT") return "PERIMETER"
    if (step === "SELECT") return "DESCRIPTION"
    if (step === "OVERVIEW") return undefined
    return step as WizardStep
  }

  function getWizardOwlText() {
    if (step === "SELECT") {
      return descriptionStartMode === "DESCRIBE" ? getDescriptionDescribeOwlText() : getDescriptionChoiceOwlText()
    }
    if (step === "DESCRIPTION") return getDescriptionStepOwlText()
    if (step === "SEVERITY") return "Quel est le niveau de gravité de ce risque ?"
    if (step === "FREQUENCY") return "À quelle fréquence les personnes y sont-elles exposées ?"
    if (step === "PREVENTION") {
      return actionsForRisk.length ? PREVENTION_DETAILS_WIZARD_OWL_TEXT : PREVENTION_EMPTY_WIZARD_OWL_TEXT
    }
    if (step === "EVALUATION") return "Comment évaluez-vous le niveau de prévention de ce risque ?"
    if (step === "OVERVIEW") return isReadonly ? undefined : "Cliquez sur une carte pour modifier les valeurs."
    if (getActiveWizardStepId() !== "PERIMETER") return undefined
    if (workUnitType === "population" || context.type === "population") {
      return "Quelles fonctions sont concernées par ce risque ?"
    }
    if (workUnitType === "site" || context.type === "site") return "Dans quels lieux ce risque est-il présent ?"
    return PERIMETER_WIZARD_OWL_TEXT
  }

  function getDescriptionChoiceOwlText() {
    return `Comment souhaitez-vous ajouter un risque ${getPerimeterOwlTextSuffix()} ?`
  }

  function getDescriptionDescribeOwlText() {
    return `${DESCRIPTION_WIZARD_OWL_TEXT} ${getPerimeterOwlTextSuffix()}`
  }

  function hasDescriptionInformation() {
    return (
      draftRisk.cAttributes.some((attribute) => attribute.value.trim()) ||
      Boolean(draftRisk.RiskFamilyId) ||
      Boolean(draftRisk.photoId)
    )
  }

  function getDescriptionStepOwlText() {
    if (!hasDescriptionInformation()) {
      return "Décrivez le risque pour compléter la fiche."
    }

    return "Les informations ci-dessous sont-elles exactes ?"
  }

  function getPerimeterOwlTextSuffix() {
    if (linkedSites.length > 1) {
      return `pour les lieux de travail ${formatQuotedList(linkedSites.map((site) => site.name))}`
    }

    if (linkedSites.length === 1) {
      return `pour le lieu de travail « ${linkedSites[0].name} »`
    }

    if (linkedPopulations.length > 1) {
      return `pour les fonctions ${formatQuotedList(linkedPopulations.map((population) => population.name))}`
    }

    if (linkedPopulations.length === 1) {
      return `pour la fonction « ${linkedPopulations[0].name} »`
    }

    return `pour l'établissement « ${activeEstablishment?.name ?? ""} »`
  }

  function formatQuotedList(values: string[]) {
    return values.map((value) => `« ${value} »`).join(", ")
  }

  //Une mesure cochee "faite" depuis le recap : l'assistant propose un niveau, l'etape Evaluation
  //le soumet a l'utilisateur. Meme comportement que depuis la carte du risque.
  function handleReviewPrevention(suggestedPreventionValue: number) {
    dispatch(physicalRiskStore.thunks.edit({ key: "prevention", value: suggestedPreventionValue }))
    setStep("EVALUATION")
  }

  function handleWizardStepClick(id: string) {
    if (id === "PERIMETER" && isNewRisk) {
      setStep("WORK_UNIT")
      return
    }
    setStep(id as WizardStep)
  }

  //Le premier attribut porte le nom du risque (cName) : sans lui la fiche n'est pas identifiable,
  //on verrouille donc les etapes qui suivent la Description. L'etape courante n'est jamais
  //verrouillee, sinon ouvrir un risque incomplet afficherait une etape grisee deja a l'ecran.
  function getStepLockReason(id: WizardStep) {
    if (id === getActiveWizardStepId() || draftRisk.cName.trim()) return undefined

    const stepIndex = WIZARD_STEPS.findIndex((wizardStep) => wizardStep.id === id)
    const descriptionIndex = WIZARD_STEPS.findIndex((wizardStep) => wizardStep.id === "DESCRIPTION")
    if (stepIndex <= descriptionIndex) return undefined

    const requiredAttribute = draftRisk.cAttributes[0]
    return getWizardStepLockReason(
      requiredAttribute ? `« ${requiredAttribute.name} »` : "le nom du risque",
      WIZARD_STEPS[stepIndex].label
    )
  }

  function goToOverview() {
    setStep("OVERVIEW")
  }

  async function goToNextWizardStep() {
    const nextWizardStep = getNextWizardStep()

    if (!nextWizardStep) {
      if (!isAnyStepLocked()) goToOverview()
      return
    }

    if (getStepLockReason(nextWizardStep)) return

    //Les mesures en place ont change pendant l'etape : l'assistant propose un niveau, que
    //l'etape Evaluation affiche ensuite pour validation
    if (step === "PREVENTION" && preventionSignature.current !== getInPlacePreventionSignature(actionsForRisk)) {
      const suggestedValue = await suggestPreventionLevel(
        draftRisk,
        actionsForRisk,
        isPreventionOnlyReinforced(preventionInPlaceIds.current, actionsForRisk) ? draftRisk.prevention : undefined
      )
      if (suggestedValue !== null) {
        dispatch(physicalRiskStore.thunks.edit({ key: "prevention", value: suggestedValue }))
      }
    }

    setStep(nextWizardStep)
  }

  //Les etapes ne modifient que le brouillon : quitter sans enregistrer le restaure
  function handleCancelAndClose() {
    dispatch(physicalRiskStore.actions.cancel())
    props.onClose()
  }

  function isAnyStepLocked() {
    return WIZARD_STEPS.some((wizardStep) => Boolean(getStepLockReason(wizardStep.id)))
  }

  function getNextWizardStep() {
    const currentStepIndex = WIZARD_STEPS.findIndex((wizardStep) => wizardStep.id === getActiveWizardStepId())
    return WIZARD_STEPS[currentStepIndex + 1]?.id
  }

  function getWizardFooter() {
    if (isReadonly) {
      return (
        <Button isWithBorder onClick={handleCancelAndClose}>
          Fermer
        </Button>
      )
    }

    //Vue d'ensemble : le seul endroit qui enregistre, et la porte de sortie
    if (step === "OVERVIEW") {
      return (
        <>
          <Button isWithBorder onClick={handleCancelAndClose}>
            Annuler
          </Button>
          <Button className="primary" onClick={handleSaveAndClose} isLoading={status === STATUS_SAVING}>
            Enregistrer
          </Button>
        </>
      )
    }

    const nextWizardStep = getNextWizardStep()

    //En modification, une etape se valide et rend la main au recapitulatif, sans requete
    if (!isNewRisk) {
      return (
        <Button className="primary" onClick={goToOverview}>
          Valider
        </Button>
      )
    }

    return (
      <>
        <Button
          className="primary"
          disabled={Boolean(nextWizardStep ? getStepLockReason(nextWizardStep) : isAnyStepLocked())}
          onClick={goToNextWizardStep}>
          {nextWizardStep ? "Continuer" : "Terminer"}
        </Button>
      </>
    )
  }

  function getWizardSidebarFooter() {
    if (isNewRisk) return undefined

    return (
      <div className="flex">
        <Space />
        <Button isWithBorder icon={faTrashAlt} isFullWidth onClick={() => setChildModal("DELETE")}>
          Supprimer le risque
        </Button>
        <Space />
      </div>
    )
  }

  function renderWizardModal({
    children,
    footer,
    footerStart,
    onWizardBack,
    title = "Ajouter un risque",
    withSidebarFooter = false
  }: {
    children: ReactNode
    footer?: ReactNode
    footerStart?: ReactNode
    onWizardBack?: () => void //Retour interne a l'etape, quand il n'y a pas d'etape precedente
    title?: string
    withSidebarFooter?: boolean
  }) {
    return (
      <Modal
        title={title}
        isFullscreen
        disableClickOutside
        disableKeyEvents
        onClose={handleCancelAndClose}
        wizardSteps={WIZARD_STEPS.map((wizardStep) => ({
          ...wizardStep,
          lockReason: getStepLockReason(wizardStep.id)
        }))}
        activeWizardStepId={getActiveWizardStepId()}
        isWizardOverview={step === "OVERVIEW"}
        onWizardStepClick={isReadonly ? undefined : handleWizardStepClick}
        wizardOwlText={getWizardOwlText()}
        wizardSidebarFooter={withSidebarFooter && !isReadonly ? getWizardSidebarFooter() : undefined}
        isWizardBackVisible={isNewRisk}
        onWizardBack={onWizardBack}
        footerStart={footerStart}
        footer={footer}>
        {children}
      </Modal>
    )
  }

  function renderEditModals() {
    return (
      <>
        {childModal === "DELETE" && (
          <PhysicalRiskDeleteModal
            onClose={() => setChildModal(null)}
            onDelete={() => {
              setChildModal(null)
              props.onClose()
            }}
          />
        )}

        {childModal === "PREVENTION_ACTION_ADD" && (
          <PreventionActionAddModal isPreventionReviewDisabled onClose={() => setChildModal(null)} />
        )}

        {childModal === "EDIT_FAMILY" && (
          <Modal
            title="Famille de risques"
            isFullScreen
            onClose={() => setChildModal(null)}
            isCloseButtonVisible
            footer={
              <Button icon={faPlus} isWithBorder onClick={() => setChildModal("ADD_FAMILY")}>
                Ajouter famille
              </Button>
            }>
            <div className="flex flex-wrap justify-center gap-x-10 px-20">
              <RiskFamilyCard
                family={createEmptyRiskFamily({ name: "Non défini" })}
                onClick={() => handleSelectFamily(null)}
              />
              {riskFamilies.map((family) => (
                <RiskFamilyCard family={family} key={family.id} onClick={() => handleSelectFamily(family.id)} />
              ))}
            </div>
          </Modal>
        )}

        {childModal === "ADD_FAMILY" && <RiskFamilyEditModal onClose={() => setChildModal("EDIT_FAMILY")} />}
      </>
    )
  }

  if (isReadonly) {
    return (
      <Modal
        title="Fiche du risque"
        isFullscreen
        isCloseButtonVisible
        footerStart={<PhysicalRiskCalculationsToggle />}
        onClose={handleCancelAndClose}>
        <PhysicalRiskOverviewStep isReadonly />
      </Modal>
    )
  }

  //La vue d'ensemble editable utilise la meme coquille de wizard
  if (step === "OVERVIEW" || WIZARD_STEPS.some((wizardStep) => wizardStep.id === step)) {
    return (
      <>
        {renderEditModals()}
        {renderWizardModal({
          title: isNewRisk ? "Ajouter un risque" : "Modifier un risque",
          footer: getWizardFooter(),
          footerStart: ["SEVERITY", "FREQUENCY", "EVALUATION", "OVERVIEW"].includes(step) ? (
            <PhysicalRiskCalculationsToggle />
          ) : undefined,
          withSidebarFooter: true,
          children: (
            <FadeIn key={step} className="flex min-h-0 flex-1 flex-col">
              {renderWizardStep()}
            </FadeIn>
          )
        })}
        {isAssistantLoading && <LoadingModal owlAssistantText={assistantLoadingText} />}
        {isSuggestingPrevention && (
          <LoadingModal owlAssistantText="Je regarde les mesures en place et je propose un niveau de prévention adapté." />
        )}
      </>
    )
  }

  if (step === "WORK_UNIT") {
    const hasSelectedWorkUnits = linkedSites.length > 0 || linkedPopulations.length > 0

    return (
      <>
        {renderWizardModal({
          footer:
            workUnitType && hasSelectedWorkUnits ? (
              <Button className="primary" onClick={() => setStep("SELECT")}>
                Continuer
              </Button>
            ) : undefined,
          onWizardBack: workUnitType ? () => setWorkUnitType(null) : undefined,
          children: (
            <PhysicalRiskWorkUnitStep
              linkedPopulations={linkedPopulations}
              linkedSites={linkedSites}
              onChange={handleWorkUnitsChange}
              onContinue={() => setStep("SELECT")}
              onUnitTypeChange={setWorkUnitType}
              unitType={workUnitType}
            />
          )
        })}
        {isAssistantLoading && <LoadingModal owlAssistantText={assistantLoadingText} />}
      </>
    )
  }

  if (step === "SELECT") {
    return (
      <>
        {renderWizardModal({
          footer: (
            <>
              <Button isWithBorder onClick={openBlankModal}>
                Passer cette étape
              </Button>
              {descriptionStartMode === "DESCRIBE" && (
                <Button
                  className="primary"
                  icon={faWandMagicSparkles}
                  disabled={!description.trim()}
                  onClick={handleGenerateFromDescription}>
                  Continuer
                </Button>
              )}
            </>
          ),
          children: (
            <PhysicalRiskSelectStep
              description={description}
              descriptionPlaceholder={descriptionPlaceholder}
              isChoiceVisible={descriptionStartMode === "CHOICE"}
              isPopulationPerimeter={linkedPopulations.length > 0 && linkedSites.length === 0}
              onDescriptionChange={setDescription}
              onSelectMode={handleSelectDescriptionStartMode}
            />
          )
        })}
        {isAssistantLoading && <LoadingModal owlAssistantText={assistantLoadingText} />}
      </>
    )
  }

  if (step === "CATALOG") {
    return (
      <PhysicalRiskPopulationCatalogModal
        siteId={linkedSites[0]?.id ?? null}
        siteName={linkedSites[0]?.name ?? null}
        populationId={linkedPopulations[0]?.id ?? null}
        populationName={linkedPopulations[0]?.name ?? null}
        familyId={context.type === "family" ? context.family.id : null}
        familyName={context.type === "family" ? context.family.name : null}
        industryCode={activeEstablishment?.industryCode}
        onClose={() => props.onClose()}
        onNext={handleStandaloneRiskCreated}
      />
    )
  }

  if (step === "PHOTO")
    return (
      <>
        <UploadPhotoModal onClose={() => props.onClose()} onUpload={(id) => handleUploadImage(id)} />
        {isAssistantLoading && <LoadingModal owlAssistantText={assistantLoadingText} />}
      </>
    )
}
